/* eslint-disable */
import OktaAuth from '@okta/okta-auth-js'
import jwtDecode from 'jwt-decode';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import {
  AUTH_SERVICE_ROLE_ADMIN,
  AUTH_SERVICE_ROLE_BATCHER,
  AUTH_SERVICE_ROLE_STANDARD,
  AUTH_SERVICE_ROLE_SUPER_ADMIN,
} from '../../../../Constants/Auth/Auth.constants';
import { I18N_LANG_AR_AE, I18N_LANG_EN_AU, I18N_LANG_ZH_CN } from '../../../../Constants/I18n/I18n.constants';
import { USER_SERVICE_PRESENCE_STATUS_PRESENT } from '../../../../Constants/User/User.constants';
import throwAuthCouldNotRefreshAccessTokenWhilstNotLoggedInError from '../../../../Errors/Auth/AuthCouldNotRefreshAccessTokenWhilstNotLoggedIn.error';
import throwAuthLoggedInButStatusNotSuccess from '../../../../Errors/Auth/AuthLoggedInButStatusNotSuccess.error';
import throwOktaInvalidTokensError from '../../../../Errors/Auth/AuthOktaInvalidTokens.errors';
import throwAuthInvalidUserLangError from '../../../../Errors/Auth/AuthOktaUnknownLangType.error';
import throwAuthOktaUnknownTransactionTypeError from '../../../../Errors/Auth/AuthOktaUnknownTransactionType.error';
import envGet from '../../../../Support/Env/Get/EnvGet.support';
import isString from '../../../../Support/Is/String/IsString.support';
import AuthAdapter from '../Auth.adapter';
import { AUTH_OKTA_TRANSACTION_SUCCESS } from './AuthOkta.adapter.constants';

const baseUrl = envGet({ key: `REACT_APP_ENTRY_PORTAL_CUSTOMER_OKTA_BASE_URL` });
const baseApiUrl = envGet({ key: `REACT_APP_BORAL_BASE_URL` });

const additionalRequestedScopes = envGet({ key: `REACT_APP_ENTRY_PORTAL_CUSTOMER_OKTA_ADDITIONAL_REQUESTED_SCOPES`, parse: true });

/**
 * AuthOktaAdapter
 * @class AuthOktaAdapter
 * @implements {AuthServiceAdapterContract}
 * @extends {AuthAdapter}
 */
class AuthOktaAdapter extends AuthAdapter {
  /**
   * The OktaAuth client
   * @type {any}
   * @memberof AuthOktaAdapter
   */
  _authClient;

  /**
   * The AuthOktaAdapterAdapter specific config
   * @type {AuthOktaAdapterConfig}
   * @memberof AuthOktaAdapterAdapter
   */
  _config;

  /**
   * How long the _token_'s should live
   * @type {number} The amount of time in milliseconds
   * @memberof AuthOktaAdapterAdapter
   */
  _tokenDuration = 60 * 60 * 1000; // 60 minutes

  /**
   * The `HttpService`
   * @type {HttpService}
   * @memberof AuthOktaAdapter
   */
  httpService;

  /**
   * Creates an instance of AuthOktaAdapter
   * @param {AuthOktaAdapterCreateDto} dto The params required to create an instance of `AuthOktaAdapter`
   * @memberof AuthOktaAdapter
   */
  constructor({ config, httpService, storageService }) {
    super({ storageService });
    this._authClient = new OktaAuth(config);
    this._config = config;
    this.httpService = httpService;
  }

  /**
   * Create a variety of tokens from an Okta `transaction`
   * @param {AuthOktaAdapterTransactionDto} dto The params required to create tokens
   * @returns {Promise<{ accessToken: AuthEntityTokenAccess, idToken: AuthEntityTokenId, sessionToken: AuthEntityTokenSession }>} The created tokens
   * @memberof AuthOktaAdapter
   */
  _createTokensFromTransaction = async ({ transaction }) => {
    const requestedTokens = this.getResponseTypes();
    const requestedScopes = this.getScopes();
    /** @type {[AuthOktaAdapterTokenAccess, AuthOktaAdapterTokenId]} */
    const tokens = await this._authClient.token.getWithoutPrompt({
      responseType: requestedTokens,
      scopes: requestedScopes,
      sessionToken: transaction.sessionToken,
    });
    if (!Array.isArray(tokens) || tokens.length !== requestedTokens.length) {
      return throwOktaInvalidTokensError(tokens);
    }
    const [oktaAccessToken, oktaIdToken] = tokens;
    this._authClient.tokenManager.add(`accessToken`, oktaAccessToken);
    const accessToken = await this._toTokenAccess(oktaAccessToken);
    const idToken = this._toTokenId(oktaIdToken);
    const sessionToken = this._toTokenSession(transaction);
    return { accessToken, idToken, sessionToken };
  };

  /**
   * Create an Okta `transaction` from a `username` and `password`
   * @param {AuthServiceLoginWithUsernameAndPasswordDto} dto The required params to create a `transaction`
   * @returns {Promise<AuthOktaAdapterTransaction>} The Okta Auth `transaction`
   * @memberof AuthOktaAdapter
   */
  _createTransactionFromUsernameAndPassword = async ({ password, username }) => {
    /** @type {Error} */
    let transactionError;
    /** @type {AuthOktaAdapterTransaction} */
    let transaction;
    try {
      transaction = await this._authClient.signIn({
        password,
        username,
      });
    } catch (error) {
      transactionError = error;
    }
    const status = get(transaction, `status`);
    if (status !== AUTH_OKTA_TRANSACTION_SUCCESS) {
      return throwAuthOktaUnknownTransactionTypeError(status, transactionError);
    }
    return transaction;
  };

  /**
   * Convert Okta's group representation into known groups.
   * @param {any} accessToken The access token
   * @returns {I18nEntityLangs} The transformed lang
   * @memberof AuthOktaAdapter
   */
  _toGroups = (accessToken) => {
    const decodedAccessToken = jwtDecode(accessToken);
    // This code needs to be backward compatible so needs
    // to support the existing billToAccounts as well
    // as the new billToAccount1, billToAccount2 ... etc.
    const legacyGroups = get(decodedAccessToken, `billToAccounts`, []);
    if (!isEmpty(legacyGroups)) {
      return legacyGroups;
    }
    const groups = pickBy(decodedAccessToken, (value, key) => /^billToAccount\d+$/.test(key));
    return Object.values(groups);
  };

  /**
   * Convert Okta's langs into known `I18nEntityLangs`
   * @param {any} lang Okta's own lang definitions
   * @returns {I18nEntityLangs} The transformed lang
   * @memberof AuthOktaAdapter
   */
  _toLang = (lang) => {
    if (!isString(lang)) {
      return;
    }
    if (lang === I18N_LANG_AR_AE || lang === `ar` || lang === `ar_AE`) {
      return I18N_LANG_AR_AE;
    }
    if (lang === I18N_LANG_EN_AU || lang === `en` || lang === `en_AU`) {
      return I18N_LANG_EN_AU;
    }
    if (lang === I18N_LANG_ZH_CN || lang === `zh` || lang === `zh_CN`) {
      return I18N_LANG_ZH_CN;
    }
    return throwAuthInvalidUserLangError(lang);
  };

  /**
   * Convert the provided roles into a known set of `roles`
   * @param {Array<string>} roles The `roles` to convert
   * @returns {Array<AuthServiceRoles>} The converted `roles`
   * @memberof AuthOktaAdapter
   */
  _toRoles = (roles) => {
    return roles.reduce((prev, next) => {
      switch (next) {
        case `CustomerPortal Admin`: {
          if (prev.includes(AUTH_SERVICE_ROLE_SUPER_ADMIN)) {
            return prev;
          }
          return [...prev, AUTH_SERVICE_ROLE_SUPER_ADMIN];
        }
        case `Admin`:
        // fall through
        case `CustomerPortal SuperUser`: {
          if (prev.includes(AUTH_SERVICE_ROLE_ADMIN)) {
            return prev;
          }
          return [...prev, AUTH_SERVICE_ROLE_ADMIN];
        }
        case `CustomerPortal StandardUser`: {
          if (prev.includes(AUTH_SERVICE_ROLE_STANDARD)) {
            return prev;
          }
          return [...prev, AUTH_SERVICE_ROLE_STANDARD];
        }
        case `Batcher`:
        // fall through
        case `Batchers`: {
          if (prev.includes(AUTH_SERVICE_ROLE_BATCHER)) {
            return prev;
          }
          return [...prev, AUTH_SERVICE_ROLE_BATCHER];
        }
        default: {
          return prev;
        }
      }
    }, []);
  };

  /**
   * Convert Okta's _accessToken_ into a known `accessToken`
   * @param {AuthOktaAdapterTokenAccess} accessToken Okta's own _accessToken_
   * @returns {Promise<AuthEntityTokenAccess>} The transformed `accessToken`
   * @memberof AuthOktaAdapter
   */
  _toTokenAccess = async (accessToken) => {
    /** @type {string} */
    const decodedAccessToken = await this._authClient.token.decode(accessToken.accessToken);
    /** @type {Array<string>} */
    const roles = get(decodedAccessToken, `payload.roles`, []);
    const groups = get(decodedAccessToken, `payload.billToAccounts`, []);
    return {
      authorizeUrl: accessToken.authorizeUrl,
      claims: {},
      expiresAt: accessToken.expiresAt * 1000,
      groups,
      roles: this._toRoles(roles),
      scopes: accessToken.scopes,
      token: accessToken.accessToken,
      type: accessToken.tokenType,
      userinfoUrl: accessToken.userinfoUrl,
    };
  };

  /**
   * Convert Okta's _idToken_ into a known `idToken`
   * @param {AuthOktaAdapterTokenId} idToken Okta's own _idToken_
   * @returns {AuthEntityTokenId} The transformed `idToken`
   * @memberof AuthOktaAdapter
   */
  _toTokenId = (idToken) => {
    const iat = get(idToken, `claims.iat`, Date.now() / 1000) * 1000;
    const iss = get(idToken, `claims.iss`, idToken.issuer);
    return {
      aud: idToken.clientId,
      authorizeUrl: idToken.authorizeUrl,
      claims: idToken.claims,
      expiresAt: idToken.expiresAt * 1000,
      iat,
      iss,
      scopes: idToken.scopes,
      token: idToken.idToken,
      type: `Bearer`,
    };
  };

  /**
   * Convert Okta's _transaction_ into a known `sessionToken`
   * @param {AuthOktaAdapterTransaction} transaction Okta's own _transaction_
   * @returns {AuthEntityTokenSession} The transformed `sessionToken`
   * @memberof AuthOktaAdapter
   */
  _toTokenSession = (transaction) => {
    const expiresAt = Number(new Date(transaction.expiresAt));
    return {
      expiresAt,
      sessions: [
        {
          current: true,
          expiresAt,
          token: transaction.sessionToken,
        },
      ],
      token: transaction.sessionToken,
      type: `SSWS`,
    };
  };

  /**
   * Convert Okta's user Object into a known `UserEntity`
   * @param {AuthOktaAdapterTransactionUser} user Okta's user Object
   * @returns {UserEntity} The transformed user
   * @memberof AuthOktaAdapter
   */
  _toUser = (user) => {
    const { id, primaryPhone } = user;
    const {
      displayName: _displayName,
      email,
      firstName,
      lastName,
      locale: _lang,
      login,
      middleName,
      mobilePhone,
      pictureUrl: avatarUrl,
      postalAddress: _postalAddress,
      streetAddress: _streetAddress,
      timeZone,
      CustomerRoleType: jobTitle,
    } = user.profile || {
      // THIS IS A GOOD CANDIDATE FOR REFACTORING AS MAYBE THERES BETTER WAY O)F PULLING THESE DETAILS // for some reason on safari 12.x (fine on 13+) user.profile details fail from network so we need to pull these details from other call if that happens
      email: user.email,
      firstName: user.given_name,
      lastName: user.family_name,
      mobilePhone: user.mobilePhone,
    };
    const displayName = _displayName || [firstName, lastName].filter(Boolean).join(` `);
    return {
      avatarUrl,
      displayName,
      email,
      firstName,
      fullName: [firstName, middleName, lastName].filter(Boolean).join(` `),
      id,
      initials: isString(firstName) && isString(lastName) ? `${firstName[0]}${lastName[0]}` : displayName.substr(0, 2),
      jobTitle,
      lang: this._toLang(_lang),
      lastName,
      login,
      middleName,
      mobilePhone,
      postalAddress: _postalAddress && {
        displayName: _postalAddress,
        id: _postalAddress,
      },
      presence: USER_SERVICE_PRESENCE_STATUS_PRESENT,
      primaryPhone,
      streetAddress: _streetAddress && {
        displayName: _streetAddress,
        id: _streetAddress,
      },
      timeZone,
      username: login,
    };
  };

  /**
   * Adds the tokens into the storage. Does not log in the user.
   * @param {any} res The response
   * @param {any} tokenManager The token manager
   * @returns {void} void
   * @memberof AuthOktaAdapter
   */
  addTokens = (res, tokenManager = this._authClient.tokenManager) => {
    // console.log(`addTokens`, res)
    // Object.values(res).map((tokenObject) => {
    //   const [key] = Object.keys(tokenObject);
    //   if (key === `accessToken` || key === `idToken`) {
    //     return tokenManager.add(key, tokenObject);
    //   }
    //   return null;
    // });
    tokenManager.add('accessToken', res.tokens.accessToken)
    tokenManager.add('idToken', res.tokens.idToken)
    // console.log(`addTokensEnd`, res)
  };

  /**
   * Clears auth from storage. Does not log out the user.
   * @returns {void} void
   * @memberof AuthOktaAdapter
   */
  clearAuth = async () => {
    await this._authClient.tokenManager.clear();
    return;
  };

  /**
   * Returns the groups
   * @returns {Array<string>} The groups
   * @memberof AuthService
   */
  getGroups = () => {
    const auth = this.getAuth();
    if (auth === null) {
      return null;
    }
    const {
      accessToken: { groups },
    } = auth;
    return groups;
  };

  /**
   * Returns the user by id
   * @param {string} id The user id
   * @returns {Array<string>} The response typed
   * @memberof AuthOktaAdapter
   */
  getUser = async (id) => {
    let newId = id;
    if (newId === undefined) {
      newId = this.getUserId();
    }

    const headers = this.createHeaders();
    // this needs to be refactored as two calls should not be needed. Needs more investigation though and we need to deploy this fix today to prod
    const url = `${baseUrl}/oauth2/default/v1/userinfo`;
    const url2 = `${baseUrl}/api/v1/users/${id || newId || `me`}`; // || newId
    const userInfo = await this.httpService.get(url, { headers });
    const userInfo2 = await this.httpService
      .get(url2, {
        withCredentials: true,
      })
      .catch((ex) => {
        // on safari 12 it happens that call sometimes might not come back (fine on 13 though). We need to catch this and
        // take care of it. A GOOD CANDIDATE FOR REFACTOTHIRNG as this caght be redundant if we got all info from userInfo endpoint
        return false;
      });

    const mergedInfo = await Promise.all([userInfo, userInfo2]);
    if (!mergedInfo[1]) {
      return this._toUser({ ...mergedInfo[0] });
    }
    return this._toUser({ ...mergedInfo[0], ...mergedInfo[1] });
  };

  /**
   * Updates the users profile by the user id
   * @param {JSON} profile The user id
   * @returns {Array<string>} The response typed
   * @memberof AuthOktaAdapter
   */
  updateUserProfile = async (profile) => {
    const headers = this.createHeaders();
    const url = `${baseApiUrl}/order-service/users?userId=${profile.id}`;
    const userInfo = await this.httpService.post(url, {
      data: {
        profile: {
          email: profile.email,
          firstName: profile.firstName === undefined ? null : profile.firstName,
          jobTitle: profile.jobTitle === undefined ? null : profile.jobTitle,
          lastName: profile.lastName === undefined ? null : profile.lastName,
          mobilePhone: profile.mobilePhone === undefined ? null : profile.mobilePhone,
          primaryPhone: profile.primaryPhone === undefined ? null : profile.primaryPhone,
        },
      },
      headers,
    });
    return this._toUser(userInfo);
  };

  /**
   * Returns the user id from auth
   * @returns {string} The user id
   * @memberof AuthOktaAdapter
   */
  getUserId = () => {
    const auth = this.getAuth();
    if (auth === null) {
      return null;
    }
    const {
      idToken: {
        claims: { sub },
      },
    } = auth;
    return sub;
  };

  /**
   * Returns the response types for the auth
   * @returns {Array<string>} The response typed
   * @memberof AuthOktaAdapter
   */
  getResponseTypes = () => {
    return [`token`, `id_token`];
  };

  /**
   * Returns the scopes for the auth
   * @returns {Array<string>} The scopes
   * @memberof AuthOktaAdapter
   */
  getScopes = () => {
    return [`email`, `openid`, `profile`, ...additionalRequestedScopes];
  };

  /**
   * Handle login success.
   * @param {any} response The auth success response
   * @param {any} tokenManager The token manager
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof AuthOktaAdapter
   */
  loginSuccess = (response, tokenManager) => {
    // console.log(`AuthOkta.adapter.jsx`, response)
    const { status } = response;
    if (status !== `SUCCESS`) {
      return throwAuthLoggedInButStatusNotSuccess(status);
    }
    this.addTokens(response, tokenManager);
    // console.log(`Before onAuthSuccess`, response)
    return this.onAuthSuccess(response);
  };

  /**
   * Attempt to login a user with an `username` and a `password`
   * @param {AuthServiceLoginWithUsernameAndPasswordDto} dto The required params to login
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof AuthOktaAdapter
   */
  loginWithUsernameAndPassword = async ({ password, username }) => {
    const transaction = await this._createTransactionFromUsernameAndPassword({ password, username });
    const { accessToken, idToken, sessionToken } = await this._createTokensFromTransaction({ transaction });
    /** @type {AuthEntity} */
    const newAuth = {
      accessToken,
      id: accessToken.token,
      idToken,
      sessionToken,
      user: this._toUser(transaction.user),
    };
    return this._changeState(newAuth);
  };

  /**
   * Logout a user
   * @returns {Promise<null>} The updated representation of the current user
   * @memberof AuthOktaAdapter
   */
  logout = async () => {
    const newAuth = null;
    try {
      await this._authClient.revokeAccessToken()
      await this._authClient.closeSession()
      // await this._authClient.tokenManager.clear();
    } catch (error) {
      /* do nothing */
    }
    return this._changeState(newAuth);
  };

  /**
   * Refresh auth for a user
   * @returns {Promise<AuthEntity>} The updated representation of the current user
   * @memberof AuthOktaAdapter
   */
  refreshAccessToken = async () => {
    if (!this.isAuthenticated()) {
      return throwAuthCouldNotRefreshAccessTokenWhilstNotLoggedInError();
    }
    try {
      const oktaAccessToken = await this._authClient.tokenManager.renew(`accessToken`);
      const accessToken = await this._toTokenAccess(oktaAccessToken);
      /** @type {AuthEntity} */
      const newAuth = {
        ...this.getAuth(),
        accessToken,
        id: accessToken.token,
      };
      return this._changeState(newAuth);
    } catch (error) {
      /* do nothing */
    }
  };

  /**
   * Update auth service on auth success.
   * Call this when using a 3rd party auth service with a success/fail callback
   * @param {any} response The response from auth
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof AuthOktaAdapter
   */
  onAuthSuccess = async (response) => {
    // @TODO, wrong access to data
    // .then(function (res) {
    //   oktaSignIn.authClient.tokenManager.add('idToken', res.tokens.idToken);
    //   oktaSignIn.authClient.tokenManager.add('accessToken', res.tokens.accessToken);
    // })
    const { accessToken: oktaAccessToken, idToken: oktaIdToken } = response.tokens;
    const userInfo = await this._authClient.token.getUserInfo(oktaAccessToken);
    const { accessToken } = oktaAccessToken;
    const idToken = this._toTokenId(oktaIdToken);
    const decodedAccessToken = jwtDecode(accessToken);
    const roles = get(decodedAccessToken, `roles`, []);
    const groups = this._toGroups(accessToken);
    const tokenAccess = {
      authorizeUrl: oktaAccessToken.authorizeUrl,
      claims: {},
      expiresAt: oktaAccessToken.expiresAt * 1000,
      groups,
      roles: this._toRoles(roles),
      scopes: oktaAccessToken.scopes,
      token: oktaAccessToken.accessToken,
      type: oktaAccessToken.tokenType,
      userinfoUrl: oktaAccessToken.userinfoUrl,
    };
    // Just following the _toUser for backwards compatibility/
    // Need to assess if _toUser has the correct fields.
    // Reference: https://developer.okta.com/docs/api/resources/oidc#userinfo
    const { family_name: lastName, given_name: firstName, name: displayName } = userInfo;
    const user = {
      displayName,
      email: userInfo.email,
      firstName,
      id: userInfo.sub,
      initials: isString(firstName) && isString(lastName) ? `${firstName[0]}${lastName[0]}` : displayName.substr(0, 2),
      lang: userInfo.locale,
      lastName,
      login: userInfo.preferred_username,
      mobilePhone: userInfo.mobilePhone,
      primaryPhone: userInfo.primaryPhone,
      presence: USER_SERVICE_PRESENCE_STATUS_PRESENT,
    };
    /** @type {AuthEntity} */
    const newAuth = {
      accessToken: tokenAccess,
      id: tokenAccess.token,
      idToken,
      user,
    };
    // console.log(`newAuth`, newAuth)
    return this._changeState(newAuth);
  };
}

export default AuthOktaAdapter;
